/**
 * 路径净化。**任何从外部来的路径，进导航之前都要过这里。**
 *
 * 外部来源比想象的多：
 * - 推送负载里的 `navigate` / `target`（→ `clients.openWindow` / 应用路由）；
 * - 用户点进来的那条链接的 `location.pathname`（→ 中性化路由的首帧解析）；
 * - `history.state` 与 sessionStorage 镜像（同源可写，XSS 之后就是攻击者可写）。
 *
 * 两个已复现的走法：
 *
 * 1. **协议相对路径**。访问 `https://你的域` 后面跟两个斜杠再跟 `evil.test/steal`，
 *    `location.pathname` 就是 `//evil.test/steal`——它以 `/` 开头，"补个前导斜杠"式的
 *    校验完全放行。应用把它交给路由或 `<a href>`，用户就出站了。
 *    对"路径本身即敏感信息"的应用，这同时是路径外泄。
 *
 * 2. **`..` 归一化的二次跨源**。`/..//evil.test` 经 URL 归一化后变成 `//evil.test`，
 *    看起来是站内路径，再解析一次就跨源了。所以**输出还要再查一遍**，只查输入不够。
 *
 * 反斜杠变体（`/\evil.test`）、前导空白都被 `new URL` 归一化到同一条路，
 * 交给它判就不用自己维护一张字符黑名单——黑名单这种东西迟早漏。
 */
/** `location` 拿不到时的兜底基准（Node 单测环境）。用什么域无所谓，只要跨源判定成立。 */
const FALLBACK_BASE = 'http://pwaship.invalid/';
function currentBase() {
    try {
        // window 和 ServiceWorkerGlobalScope 都有 location
        return typeof location !== 'undefined' && location.origin && location.origin !== 'null'
            ? location.origin
            : FALLBACK_BASE;
    }
    catch {
        return FALLBACK_BASE;
    }
}
/** 结果本身还能不能被再解析成跨源（`//x` / `/\x`） */
function stillEscapes(path) {
    return path.startsWith('//') || path.startsWith('/\\');
}
/**
 * 把任意输入收敛成一条**同源的站内路径**（`/path?query#hash`）。
 * 不是字符串、跨源、解析失败、或结果仍可跨源 —— 一律返回 `fallback`。
 *
 * ⚠️ 失败方向是**回落到兜底**，不是抛错：这条路径上最常见的调用点是"点了通知要去哪"，
 * 抛错等于点了通知没反应，而回落到首页至少人是进来了。
 */
export function sanitizePath(input, fallback = '/', base = currentBase()) {
    if (typeof input !== 'string' || !input)
        return fallback;
    let baseOrigin;
    try {
        baseOrigin = new URL(base).origin;
    }
    catch {
        return fallback;
    }
    let url;
    try {
        url = new URL(input, base);
    }
    catch {
        return fallback;
    }
    if (url.origin !== baseOrigin)
        return fallback;
    const out = `${url.pathname}${url.search}${url.hash}`;
    if (!out.startsWith('/') || stillEscapes(out))
        return fallback;
    return out;
}
/** 判断一个路径能不能原样用于导航（不改写，只回答是否安全）。 */
export function isSafePath(input, base = currentBase()) {
    const sentinel = ' not-a-path';
    return sanitizePath(input, sentinel, base) !== sentinel;
}
//# sourceMappingURL=path.js.map